import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField, Switch, Box, Button, Tooltip, IconButton,
  Typography, 
  InputAdornment, Grid, 
  Card, CardContent, CardHeader
} from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

import { useWebSocket } from '../WebSocketContext';
import { isActionEnabled, buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';
import { buildForceRunPayload } from '../../Utils/forceRunUtils';

const ActionsValidateDoctorLicense = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  // Prepopulated values
  const DEFAULT_TAG = 'doctor_license_final';
  const DEFAULT_LICENSE_TAG = '';
  
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const initialSettings = useRef({});
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunResult, setForceRunResult] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [id, setId] = useState(settingId || 0);
  const [tag, setTag] = useState(DEFAULT_TAG);
  const [licenseTag, setLicenseTag] = useState(DEFAULT_LICENSE_TAG);
  const [conditions, setConditions] = useState([]);
  
  // Tags dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  
  const tagRef = useRef(null);
  const licenseTagRef = useRef(null);

  const handleConditionsChange = (newConditions) => {
    setConditions(newConditions);
    if (initialSettings.current) {
        // Trigger save check when conditions change locally
        const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
        if (onSaveStatusChange) {
             // We can't easily calculate hasChanges here without full state comparison, 
             // but useEffects will handle it.
        }
    }
  };

  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');

    if (settingId && !isTemporaryId) {
      sendMessage(buildGetExportSettingsPayload(settingId, caseid));
    } else if (isTemporaryId) {
      setId(settingId);
      setIsEnabled(false);
      setTag(DEFAULT_TAG);
      setLicenseTag(DEFAULT_LICENSE_TAG);
      setConditions([]);

      initialSettings.current = {
        id: settingId,
        enable: false,
        tag: DEFAULT_TAG,
        licenseTag: DEFAULT_LICENSE_TAG,
        useConditions: true,
        conditions: [],
        data: { name: 'Validate Doctor License' }
      };
      setIsSaveDisabled(false);
    }
  }, [caseid, settingId, sendMessage]);


  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        if (parsedMessage.command === 'getExportSettings' && parsedMessage.data.type === 'validatedoctorlicense') {
          console.log('Received export settings:', parsedMessage.data);
          const { id: newId, enable, customData } = parsedMessage.data;
          setId(newId);
          setIsEnabled(isActionEnabled(enable));
          
          if (customData.tag !== undefined) setTag(customData.tag);
          if (customData.licenseTag !== undefined) setLicenseTag(customData.licenseTag);
          if (customData?.conditions !== undefined) setConditions(customData.conditions);

          initialSettings.current = {
            id: newId,
            enable: isActionEnabled(enable),
            tag: customData.tag || DEFAULT_TAG,
            licenseTag: customData.licenseTag || '',
            useConditions: customData?.useConditions || false,
            conditions: customData?.conditions || []
          };
          setIsSaveDisabled(true);
        } else if (parsedMessage.command === 'saveActionSettings') {
          if (parsedMessage.data && parsedMessage.data.id && parsedMessage.data.type === 'validatedoctorlicense') {
            const savedId = parsedMessage.data.id;
            const savedCustomData = parsedMessage.data.customData;
            
            setId(savedId);
            initialSettings.current = {
              id: savedId,
              enable: isEnabled,
              tag,
              licenseTag,
              useConditions: true,
              conditions: [...conditions],
              data: savedCustomData || initialSettings.current.data || {}
            };
            
            if (onSaveStatusChange) { 
              onSaveStatusChange(savedId, false, typeof id === 'string' && id.startsWith('temp_') ? id : null);
            }
            
            setIsSaveDisabled(true);
          }
        } else if (parsedMessage.command === 'forceRun') {
          setForceRunLoading(false);
          setForceRunResult(parsedMessage.data);
          setPopUpOpen(true);
        } else if (parsedMessage.command === 'forceRunError') {
          setForceRunLoading(false);
          setForceRunResult({ error: parsedMessage.data });
          setPopUpOpen(true);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, isEnabled, tag, licenseTag, conditions, onSaveStatusChange, id]);

  const updateSaveDisabledState = useCallback(() => {
    if (initialSettings.current && initialSettings.current.id !== undefined) {
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);
      const currentConditionsJSON = JSON.stringify(conditions);

      const unchanged =
        isEnabled === initialSettings.current.enable &&
        tag === initialSettings.current.tag &&
        licenseTag === initialSettings.current.licenseTag &&
        initialSettings.current.useConditions === true &&
        currentConditionsJSON === initialConditionsJSON;

      const hasChanges = !unchanged;
      
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasChanges);
      }
      setIsSaveDisabled(unchanged);
    } else {
      console.log('initialSettings.current is not properly set:', initialSettings.current);
    }
  }, [isEnabled, tag, licenseTag, conditions, id, onSaveStatusChange]);

  useEffect(() => {
    updateSaveDisabledState();
  }, [isEnabled, tag, licenseTag, conditions, updateSaveDisabledState]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'tag') {
      tagRef.current?.insertTag(value);
    } else if (menuType === 'licenseTag') {
      licenseTagRef.current?.insertTag(value);
    }
    handleMainTagMenuClose();
  };

  const handleSave = () => {
    if (ws) {
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      
      const settingsData = {
        tag,
        licenseTag,
        useConditions: true,
        conditions
      };
      
      const saveData = {
        command: 'saveActionSettings',
        id: currentId,
        caseid,
        type: 'validatedoctorlicense',
        enable: isEnabled ? '1' : '0',
        interval: '0',
        data: settingsData
      };
      ws.send(JSON.stringify(saveData));
    }
  };

  const handleForceRun = (mode = 'all_events', selectedTimeeventId = null, persistent = false) => {
    if (!ws) {
      return;
    }

    try {
      setForceRunLoading(true);
      const forceRunData = buildForceRunPayload({
        caseid,
        exportId: id,
        selection: mode,
        timeeventId: selectedTimeeventId,
        persistent,
      });
      ws.send(JSON.stringify(forceRunData));
    } catch (error) {
      console.error('Failed to build force run payload:', error);
      setForceRunLoading(false);
    }
  };

  const closePopUp = () => {
    setPopUpOpen(false);
    setForceRunResult(null);
  };

  return (
    <ActionSettingsLayout
      title="Validate Doctor License"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={closePopUp}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      primaryColor="#2196f3"
    >
        {/* Configuration Section */}
        <Card sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #2196f310',
          overflow: 'hidden',
        }}>
          <CardHeader
            title={
              <Typography sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box component="span" sx={{
                  bgcolor: '#2196f3',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(33, 150, 243, 0.2)',
                }}>
                  CONFIG
                </Box>
                Doctor License Validation Settings
              </Typography>
            }
            sx={{
              borderBottom: '1px solid #f8fcff',
              backgroundColor: '#fafcff',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              p: { xs: 1.5, sm: 2.5 },
            }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                  Output Tag/Variable Name
                </Typography>
                <TagEditor
                    caseid={caseid}
                  ref={tagRef}
                  value={tag}
                  onChange={setTag}
                  placeholder="The variable name to store the validation result"
                  singleLine
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'tag')}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                  License Source Tag
                </Typography>
                <TagEditor
                    caseid={caseid}
                  ref={licenseTagRef}
                  value={licenseTag}
                  onChange={setLicenseTag}
                  placeholder="The tag containing the doctor license number (e.g. {doctor_license})"
                  singleLine
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'licenseTag')}
                />
              </Grid>
            </Grid>

            {/* Information about the fixed prompt */}
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#444' }}>
                🏥 License Validation Rules (Fixed)
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', lineHeight: 1.6 }}>
                • If license is 5 digits: Keep as is<br/>
                • If license is 6 digits and starts with 1: Remove first digit<br/>
                • If license is 6 digits and does NOT start with 1: Remove last digit<br/>
                • If license is 7 digits: Remove first and last digits
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
                This will automatically process doctor license numbers according to standardization rules.
              </Typography>
            </Box>
          </CardContent>
        </Card>

      {/* Tags Dropdown */}
      <TagsDropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        setAnchorEl={handleMainTagMenuClose}
        tags={tags}
        handleTagSelect={handleTagSelect}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default ActionsValidateDoctorLicense;

